import { cn } from "@client/utils/cn";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

function InputGroup({
	className,
	size = "lg",
	...props
}: React.ComponentProps<"div"> & { size?: "sm" | "lg" }) {
	return (
		<div
			data-slot="input-group"
			data-size={size}
			role="group"
			className={cn(
				"group/input-group relative flex w-full min-w-0 items-center gap-1 rounded-xl bg-slate-700 text-white outline-hidden transition-[box-shadow,opacity] duration-300",
				"has-[:disabled]:opacity-50",
				"has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-slate-600",
				"has-[[data-slot][aria-invalid=true]]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-red-700",
				"in-data-[slot=combobox-content]:has-[[data-slot=input-group-control]:focus-visible]:ring-0",
				"has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col",
				"has-[>textarea]:h-auto has-[>textarea]:items-stretch",
				"has-[>[data-align=inline-end]]:[&>[data-slot=input-group-control]]:pr-0 has-[>[data-align=inline-start]]:[&>[data-slot=input-group-control]]:pl-0",
				"has-[>[data-align=block-end]]:[&>input]:pt-3 has-[>[data-align=block-start]]:[&>input]:pb-3",
				className,
			)}
			{...props}
		/>
	);
}

const inputGroupAddonVariants = cva(
	"flex h-auto cursor-text items-center justify-center gap-2 text-white select-none [&>svg]:pointer-events-none [&>svg]:opacity-20 [&>svg]:transition-opacity [&>svg]:duration-300 group-focus-within/input-group:[&>svg]:opacity-100 [&>svg:not([class*='size-'])]:size-6 group-data-[size=sm]/input-group:[&>svg:not([class*='size-'])]:size-5 [&_kbd]:rounded-sm",
	{
		variants: {
			align: {
				"inline-start":
					"order-first pl-4 has-[[data-variant=default]]:pl-1.5",
				"inline-end":
					"order-last last:pr-4 has-[[data-variant=default]]:last:pr-1.5",
				"block-start": "order-first w-full justify-start px-4 pt-3",
				"block-end": "order-last w-full justify-start px-4 pb-3",
			},
		},
		defaultVariants: {
			align: "inline-start",
		},
	},
);

function InputGroupAddon({
	className,
	align = "inline-start",
	...props
}: React.ComponentProps<"div"> & VariantProps<typeof inputGroupAddonVariants>) {
	return (
		<div
			role="group"
			data-slot="input-group-addon"
			data-align={align}
			className={cn(inputGroupAddonVariants({ align }), className)}
			onClick={(e) => {
				if ((e.target as HTMLElement).closest("button")) {
					return;
				}
				e.currentTarget.parentElement
					?.querySelector<HTMLElement>(
						"[data-slot=input-group-control]",
					)
					?.focus();
			}}
			{...props}
		/>
	);
}

const inputGroupButtonVariants = cva(
	"flex shrink-0 items-center justify-center outline-none transition-[color,background-color,opacity] duration-300 [&_svg]:pointer-events-none",
	{
		variants: {
			variant: {
				default:
					"bg-slate-600 text-slate-400 hover:bg-slate-800 hover:text-white",
				ghost: "bg-transparent text-white opacity-20 group-focus-within/input-group:opacity-100",
			},
			size: {
				xs: "rounded-[6px] p-1 [&_svg:not([class*='size-'])]:size-5 group-data-[size=sm]/input-group:rounded-md group-data-[size=sm]/input-group:p-1 group-data-[size=sm]/input-group:[&_svg:not([class*='size-'])]:size-5",
				sm: "rounded-md p-1 [&_svg:not([class*='size-'])]:size-5",
				"icon-xs":
					"size-6 rounded-md p-0 [&_svg:not([class*='size-'])]:size-4",
				"icon-sm":
					"size-8 rounded-[10px] p-0 [&_svg:not([class*='size-'])]:size-5",
			},
		},
		compoundVariants: [
			{
				variant: "ghost",
				size: "xs",
				class: "rounded-none p-0",
			},
		],
		defaultVariants: {
			variant: "ghost",
			size: "xs",
		},
	},
);

function InputGroupButton({
	className,
	type = "button",
	variant = "ghost",
	size = "xs",
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof inputGroupButtonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";
	return (
		<Comp
			type={type}
			data-slot="input-group-button"
			data-size={size}
			data-variant={variant}
			className={cn(
				inputGroupButtonVariants({ size, variant }),
				className,
			)}
			{...props}
		/>
	);
}

function InputGroupText({ className, ...props }: React.ComponentProps<"span">) {
	return (
		<span
			className={cn(
				"flex items-center gap-2 mobile-body-1 text-slate-400 lg:body-1 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-6 group-data-[size=sm]/input-group:[&_svg:not([class*='size-'])]:size-5",
				className,
			)}
			{...props}
		/>
	);
}

const inputGroupControlClassName =
	"min-w-0 flex-1 self-stretch bg-slate-700 rounded-xl p-1.5 pl-4 h-10 text-left text-white shadow-none outline-hidden mobile-body-1 ring-0 placeholder:mobile-body-1 placeholder:text-slate-400 group-data-[size=sm]/input-group:py-2 focus-visible:ring-0  aria-invalid:ring-0 lg:body-1 lg:placeholder:body-1";

function InputGroupInput({
	className,
	...props
}: React.ComponentProps<"input">) {
	return (
		<input
			data-slot="input-group-control"
			className={cn(inputGroupControlClassName, className)}
			{...props}
		/>
	);
}

function InputGroupTextarea({
	className,
	...props
}: React.ComponentProps<"textarea">) {
	return (
		<textarea
			data-slot="input-group-control"
			className={cn(
				inputGroupControlClassName,
				"min-h-20 resize-none",
				className,
			)}
			{...props}
		/>
	);
}

export {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupText,
	InputGroupInput,
	InputGroupTextarea,
};
